/**
 * KAN-826 (stage 2): state of the "Организация", "Свои поля" and
 * "Дополнить уже загруженных" parts of the list upload form.
 *
 * - organization columns: prefilled from the remembered preset; once the real
 *   headers are known the page calls `autoSelect` (saved column, then header
 *   names; the contact phone column is never taken for the organization phone);
 * - custom fields: rows prefilled from the preset, add / change / remove,
 *   client-side validation (`validate`) and the server 400 messages;
 * - `requestOptions` is what goes into the upload call (only non-empty parts).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MAX_EXTRA_FIELDS,
  autoSelectCompanyFields,
  cleanCompanyFields,
  extractSectionErrors,
  toExtraFieldsPayload,
  validateExtraFields,
  type ExtraFieldDraft,
} from '@/lib/uploadCompanyFields';
import type {
  ColumnMapping,
  CompanyFieldKey,
  CompanyFieldsMapping,
  ExtraFieldMapping,
  SchemaField,
  UploadOptions,
} from '@/types/dataset';

let nextRowId = 0;
const newRowId = (): string => {
  nextRowId += 1;
  return `extra-${nextRowId}`;
};

const toDraft = (field: ExtraFieldMapping): ExtraFieldDraft => ({
  id: newRowId(),
  name: field.name ?? '',
  column: field.column ?? '',
  target: field.target === 'contact' ? 'contact' : 'company',
  to_bot: field.to_bot !== false,
});

export interface UseUploadCompanyFieldsArgs {
  schema: SchemaField[];
  savedCompanyFields: CompanyFieldsMapping;
  savedExtraFields: ExtraFieldMapping[];
}

export function useUploadCompanyFields({
  schema,
  savedCompanyFields,
  savedExtraFields,
}: UseUploadCompanyFieldsArgs) {
  const [companyFields, setCompanyFields] = useState<CompanyFieldsMapping>({});
  const [extraRows, setExtraRows] = useState<ExtraFieldDraft[]>([]);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [companyServerErrors, setCompanyServerErrors] = useState<string[]>([]);
  const [extraServerErrors, setExtraServerErrors] = useState<string[]>([]);

  // Preset prefill (before the headers are known the columns stay as saved).
  useEffect(() => {
    setCompanyFields({ ...savedCompanyFields });
  }, [savedCompanyFields]);
  useEffect(() => {
    setExtraRows(savedExtraFields.map(toDraft));
    setRowErrors({});
  }, [savedExtraFields]);

  /** Real headers known: pick organization columns, drop custom columns not in the file. */
  const autoSelect = useCallback(
    (headers: string[], contactMapping: ColumnMapping) => {
      setCompanyFields(autoSelectCompanyFields(headers, savedCompanyFields, contactMapping));
      setExtraRows((rows) =>
        rows.map((row) => (headers.includes(row.column) ? row : { ...row, column: '' }))
      );
    },
    [savedCompanyFields]
  );

  const setCompanyField = useCallback((key: CompanyFieldKey, column: string) => {
    setCompanyFields((prev) => ({ ...prev, [key]: column }));
    setCompanyServerErrors([]);
  }, []);

  const addExtraRow = useCallback(() => {
    setExtraRows((rows) =>
      rows.length >= MAX_EXTRA_FIELDS
        ? rows
        : [...rows, { id: newRowId(), name: '', column: '', target: 'company', to_bot: true }]
    );
  }, []);

  const updateExtraRow = useCallback((id: string, patch: Partial<ExtraFieldMapping>) => {
    setExtraRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    setRowErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setExtraServerErrors([]);
  }, []);

  const removeExtraRow = useCallback((id: string) => {
    setExtraRows((rows) => rows.filter((row) => row.id !== id));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  /** Client-side check of the custom fields; true when the upload may go. */
  const validate = useCallback((): boolean => {
    const errors = validateExtraFields(extraRows, schema);
    setRowErrors(errors);
    return Object.keys(errors).length === 0;
  }, [extraRows, schema]);

  const clearErrors = useCallback(() => {
    setRowErrors({});
    setCompanyServerErrors([]);
    setExtraServerErrors([]);
  }, []);

  const setServerErrors = useCallback((err: unknown) => {
    setCompanyServerErrors(extractSectionErrors(err, 'company_fields'));
    setExtraServerErrors(extractSectionErrors(err, 'extra_fields'));
  }, []);

  const requestOptions = useMemo<
    Pick<UploadOptions, 'companyFields' | 'extraFields' | 'updateExisting'>
  >(
    () => {
      // Only what the form actually has: an untouched form sends the same
      // request as before KAN-826.
      const options: Pick<UploadOptions, 'companyFields' | 'extraFields' | 'updateExisting'> = {};
      const mapped = cleanCompanyFields(companyFields);
      if (Object.keys(mapped).length > 0) options.companyFields = mapped;
      if (extraRows.length > 0) options.extraFields = toExtraFieldsPayload(extraRows);
      if (updateExisting) options.updateExisting = true;
      return options;
    },
    [companyFields, extraRows, updateExisting]
  );

  return {
    companyFields,
    setCompanyField,
    autoSelect,
    extraRows,
    addExtraRow,
    updateExtraRow,
    removeExtraRow,
    updateExisting,
    setUpdateExisting,
    rowErrors,
    companyServerErrors,
    extraServerErrors,
    validate,
    clearErrors,
    setServerErrors,
    requestOptions,
  };
}
